import * as React from 'react';
import * as acl from 'common/utils/acl';

import { EntityActions, EntityType } from 'common/entity';
import { Payment, PaymentDialog } from './PaymentDialog';
import { Refund, RefundDialog } from './RefundDialog';
import { getExportUrl, toApiError } from 'common/utils/api';

import { AddressBlock } from 'common/components/AddressBlock';
import { Adjustment } from 'src/screens/Orders/AdjustmentDialog';
import { AdjustmentDialog } from './AdjustmentDialog';
import { AppState } from 'common/models/AppState';
import { Badge } from 'common/components/Badge';
import { Card } from 'common/components/Card';
import { ChargeDialog } from './ChargeDialog';
import { ConfirmDialog } from 'common/components/dialogs/ConfirmDialog';
import { Datetime } from 'common/components/Datetime';
import { Loading } from 'common/components/Loading';
import { Money } from 'common/components/Money';
import { Order } from 'common/api/models/Order';
import { OrderApi } from 'common/api/resources/OrderApi';
import { Page } from 'common/components/layout/Page/Page';
import { SendEmailDialog } from 'common/components/dialogs/SendEmailDialog';
import { Transaction } from 'common/api/models/Transaction';
import { TransactionApi } from 'common/api/resources/TransactionApi';
import { MemberLink } from 'common/components/link/MemberLink';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { Address } from 'common/api/models/Address';
import { EditAddressDialog } from 'common/components/dialogs/EditAddressDialog';
import { Analytics } from 'common/utils/Analytics';

enum OrdersDetailsDialog {
  PAYMENT,
  SEND_EMAIL,
  SEND_LETTER,
  REFUND,
  CANCEL,
  ADJUSTMENT,
  CHARGE,
  UPDATE_SHIPPING_ADDRESS,
}

export interface OrdersDetailProps {
  appState: AppState;
  entityActions: EntityActions;
  orderNumber: number;
}
export interface OrdersDetailState {
  isLoading: boolean;
  error?: string;
  order?: Order;
  transactions: Transaction[];

  editShippingAddress?: Address;

  dialog?: OrdersDetailsDialog;
  dialogError?: string;
  dialogSuccess?: string;
  dialogLoading?: boolean;
}

export class OrdersDetail extends React.Component<OrdersDetailProps, OrdersDetailState> {
  state: OrdersDetailState = { isLoading: false, transactions: [] };

  componentDidMount() {
    this.reload(this.props.orderNumber);
  }

  componentDidUpdate(prevProps: OrdersDetailProps) {
    if (prevProps.orderNumber !== this.props.orderNumber) {
      this.reload(this.props.orderNumber);
    }
  }

  reload(orderNumber: number) {
    this.setState({ isLoading: true });
    OrderApi.getOrderByNumber(orderNumber).then(
      res => {
        this.setState({ order: res });
        this.props.entityActions.fetchIfPresent(EntityType.MEMBER, res.createId);
        this.props.entityActions.fetchIfPresent(EntityType.MEMBER, res.accountId);
        res.items!.map(i => this.props.entityActions.fetchIfPresent(EntityType.PRODUCT, i.productId));
        TransactionApi.getTransactions({ orderId: res.id }).then(
          trans => {
            trans.data.map(t => this.props.entityActions.fetchIfPresent(EntityType.MEMBER, t.createId));
            this.setState({ transactions: trans.data, isLoading: false });
          },
          err => {
            this.setState({ error: toApiError(err) });
          }
        );
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  }

  handleOpenAddPayment = () => {
    this.setState({ dialog: OrdersDetailsDialog.PAYMENT });
  };

  handleSavePayment = (payment: Payment) => {
    this.setState({ dialog: undefined });
    TransactionApi.processPayment({
      accountId: this.state.order!.accountId,
      orderId: this.state.order!.id,
      checkNumber: payment.checkNumber,
      date: payment.date,
      amount: payment.amount,
      fee: payment.fee,
    }).then(
      res => {
        Analytics.order.payment();
        this.reload(this.props.orderNumber);
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  handleCancelDialog = () => {
    this.setState({ dialog: undefined });
  };

  handleOpenSendEmail = () => {
    this.setState({
      dialog: OrdersDetailsDialog.SEND_EMAIL,
      dialogError: undefined,
      dialogSuccess: undefined,
      dialogLoading: false,
    });
  };

  handleSubmitSendEmail = (email: string) => {
    this.setState({ dialogLoading: true });
    OrderApi.sendOrderEmail(this.state.order!.id!, { email: email }).then(
      res => {
        Analytics.order.sendInvoiceEmail();
        this.setState({
          dialogSuccess: 'Email sent to ' + email + '.',
          dialogError: undefined,
          dialogLoading: false,
        });
      },
      err => {
        this.setState({ dialogError: toApiError(err), dialogSuccess: undefined, dialogLoading: false });
      }
    );
  };

  handleOpenSendLetter = () => {
    this.setState({
      dialog: OrdersDetailsDialog.SEND_LETTER,
      dialogError: undefined,
      dialogSuccess: undefined,
      dialogLoading: false,
    });
  };

  handleSubmitSendLetter = () => {
    const member = this.props.appState.entities.member[this.state.order!.accountId!];
    this.setState({ dialogLoading: true });
    OrderApi.sendOrderInvoiceLetter(this.state.order!.id!).then(
      res => {
        Analytics.order.sendInvoiceLetter();
        this.setState({
          dialogSuccess: 'Letter will be mailed to ' + member.name + '.',
          dialogError: undefined,
          dialogLoading: false,
        });
      },
      err => {
        this.setState({ dialogError: toApiError(err), dialogSuccess: undefined, dialogLoading: false });
      }
    );
  };

  handleOpenCancel = () => {
    this.setState({ dialog: OrdersDetailsDialog.CANCEL });
  };

  handleSubmitCancel = () => {
    this.setState({ dialog: undefined, isLoading: true });
    OrderApi.cancelOrder(this.state.order!.id!).then(
      res => {
        Analytics.order.cancel();
        this.reload(this.props.orderNumber);
      },
      err => {
        this.setState({ error: toApiError(err), isLoading: false });
      }
    );
  };

  handleOpenRefund = () => {
    this.setState({ dialog: OrdersDetailsDialog.REFUND });
  };

  handleSubmitRefund = (refund: Refund) => {
    const order = this.state.order!;
    this.setState({ dialogLoading: true });
    OrderApi.refundOrder(order.id!, refund).then(
      res => {
        Analytics.order.refund();
        const onSuccess = () => {
          this.setState({ dialog: undefined, dialogLoading: false, dialogError: undefined });
          this.reload(this.props.orderNumber);
        };
        const onError = (err: any) => {
          this.setState({ error: toApiError(err) });
        };
        if (refund.adjustBalance) {
          if (refund.amount >= order.billAmount!) {
            OrderApi.cancelOrder(order.id!).then(() => {
              onSuccess();
              Analytics.order.cancel();
              // tslint:disable-next-line:align
            }, onError);
          } else {
            TransactionApi.createAdjustment({
              accountId: this.state.order!.accountId,
              orderId: this.state.order!.id!,
              amount: refund.amount,
              productId: refund.productId,
              description: 'Refund',
            }).then(() => {
              onSuccess();
              Analytics.order.adjust();
              // tslint:disable-next-line:align
            }, onError);
          }
        } else {
          onSuccess();
        }
      },
      err => {
        this.setState({ dialogError: toApiError(err), dialogLoading: false });
      }
    );
  };

  handleOpenAdjustment = () => {
    this.setState({ dialog: OrdersDetailsDialog.ADJUSTMENT });
  };

  handleOpenCharge = () => {
    this.setState({ dialog: OrdersDetailsDialog.CHARGE });
  };

  handleSubmitCharge = () => {
    this.setState({ dialog: undefined });
    this.reload(this.props.orderNumber);
  };

  handleSubmitAdjustment = (adjustment: Adjustment) => {
    if (!this.state.order) {
      return;
    }
    this.setState({ dialogLoading: true });
    TransactionApi.createAdjustment({
      accountId: this.state.order.accountId,
      orderId: this.state.order.id!,
      productId: adjustment.productId,
      amount: adjustment.amount,
      description: adjustment.description,
    }).then(
      res => {
        this.setState({ dialog: undefined, dialogLoading: false, dialogError: undefined });
        Analytics.order.adjust();
        this.reload(this.props.orderNumber);
      },
      err => {
        this.setState({ dialogError: toApiError(err), dialogLoading: false });
      }
    );
  };

  handleChangeShippingAddress = () => {
    this.setState({
      dialog: OrdersDetailsDialog.UPDATE_SHIPPING_ADDRESS,
      editShippingAddress: this.state.order!.shippingAddress,
    });
  };

  handleSaveAddress = (shippingAddress: Address) => {
    OrderApi.updateOrder(this.state.order!.id!, { shippingAddress }).then(
      res => {
        this.setState({ dialog: undefined });
        Analytics.order.update();
        this.reload(this.props.orderNumber);
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  render() {
    const entities = this.props.appState.entities;

    if (this.state.isLoading || (!this.state.order && !this.state.error)) {
      return <Loading />;
    }
    if (this.state.error) {
      return <div className="alert alert-danger">{this.state.error}</div>;
    }

    const order = this.state.order!;
    const member = entities.member[order.accountId!];
    const cols = 7;

    const hasChargeTransaction = this.state.transactions.filter(t => t.type === Transaction.Type.CHARGE).length > 0;
    const refundableAmount = order.paymentAmount! - order.refundAmount!;
    const dueAmount = order.billAmount! - (order.paymentAmount! - order.refundAmount!) - order.adjustmentAmount!;

    const pdfUrl = getExportUrl('/v1/order/' + order.id + '/pdf', { download: true });
    const pdfReceiptUrl = getExportUrl('/v1/order/' + order.id + '/pdf', { download: true, receipt: true });

    return (
      <Page
        title={'Order #' + order.number}
        breadcrumbs={[{ label: 'Orders', to: '/orders' }]}
        secondaryActions={[
          {
            shouldRender: true,
            onClick: this.handleOpenSendEmail,
            label: 'Send Email',
            iconName: 'at',
          },
          {
            shouldRender: true,
            onClick: this.handleOpenSendLetter,
            label: 'Mail Letter',
            iconName: 'envelope-o',
          },
          {
            shouldRender: true,
            href: pdfUrl,
            hrefSameTab: true,
            label: 'Download Invoice',
            iconName: 'file-pdf-o',
          },
          {
            shouldRender: order.status === Order.Status.PAID,
            href: pdfReceiptUrl,
            hrefSameTab: true,
            label: 'Download Receipt',
            iconName: 'file-pdf-o',
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER),
            onClick: this.handleOpenAddPayment,
            label: 'Process Check/Wire',
            iconName: 'money',
            testId: 'ordersDetailsCheck',
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER),
            onClick: this.handleOpenCharge,
            label: 'Charge',
            iconName: 'credit-card',
            testId: 'ordersDetailsCharge',
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_REFUND),
            onClick: this.handleOpenAdjustment,
            label: 'Add Adjustment',
            iconName: 'balance-scale',
            testId: 'ordersDetailsAddAdjustment',
          },
          {
            shouldRender:
              refundableAmount > 0 && acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_REFUND),
            onClick: this.handleOpenRefund,
            label: 'Issue Refund',
            iconName: 'exchange',
            testId: 'ordersDetailsRefund',
          },
          {
            shouldRender:
              order.status === Order.Status.PENDING &&
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_REFUND),
            onClick: this.handleOpenCancel,
            label: 'Cancel Order',
            iconName: 'times',
          },
        ]}
      >
        <Card padding="1rem" marginBottom="2rem">
          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-2 text-right">
              <b>Member</b>
            </div>
            <div className="col">
              <MemberLink member={member} />
            </div>
          </div>
          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-2 text-right">
              <b>Status</b>
            </div>
            <div className="col">
              {order.status === Order.Status.PAID && <Badge type="success" text="Paid" />}
              {order.status === Order.Status.PENDING && <Badge type="warning" text="Pending" />}
              {order.status === Order.Status.CANCELLED && <Badge type="danger" text="Cancelled" />}
            </div>
          </div>
          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-2 text-right">
              <b>Placed</b>
            </div>
            <div className="col">
              <Datetime at={order.createAt} /> by <MemberLink member={entities.member[order.createId!]} />
            </div>
          </div>
          <div className="row">
            <div className="col-2 text-right">
              <b>Billing</b>
            </div>
            <div className="col-2">
              <AddressBlock address={order.billingAddress} />
            </div>
          </div>
          <div className="row">
            <div className="col-2 text-right">
              <b>Shipping</b>
            </div>
            <div className="col-2">
              <AddressBlock address={order.shippingAddress} />
            </div>
            <div className="col">
              <button
                className="btn btn-sm btn-secondary"
                onClick={this.handleChangeShippingAddress}
                data-field="shippingAddress"
              >
                Change
              </button>
            </div>
          </div>
          <h5>Items</h5>
          <table className="table table-striped table-hover" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th>Price</th>
                <th style={{ width: 20 }}>Qty</th>
                <th style={{ width: 120 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items!.map((item, i) => (
                <tr key={i}>
                  <td>{entities.product[item.productId] ? entities.product[item.productId].sku : undefined}</td>
                  <td>{item.name}</td>
                  <td>
                    <Money value={item.price} />
                  </td>
                  <td>{item.qty}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Money value={item.qty * item.price} />
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} />
                <th>Billed</th>
                <td style={{ textAlign: 'right' }}>
                  <b>
                    <Money value={order.billAmount} />
                  </b>
                </td>
              </tr>
              <tr>
                <td colSpan={3} />
                <th>Paid</th>
                <td style={{ textAlign: 'right' }}>
                  <b>
                    <Money value={order.paymentAmount! - order.refundAmount!} />
                  </b>
                </td>
              </tr>
              <tr>
                <td colSpan={3} />
                <th>Adjustments</th>
                <td style={{ textAlign: 'right' }}>
                  <b>
                    <Money value={order.adjustmentAmount} />
                  </b>
                </td>
              </tr>
              <tr>
                <td colSpan={3} />
                <th>Due</th>
                <td style={{ textAlign: 'right' }}>
                  <b>
                    <Money value={dueAmount} />
                  </b>
                </td>
              </tr>
            </tbody>
          </table>
          <h5>Transactions</h5>
          <table
            className="table table-striped table-hover"
            style={{ width: '100%' }}
            data-testid="ordersDetailsTransactionsTable"
          >
            <thead>
              <tr>
                <th>Num</th>
                <th>Amount</th>
                <th>Fee</th>
                <th>Type</th>
                <th>Details</th>
                <th>Date</th>
                <th>Processed</th>
                <th>Processed By</th>
              </tr>
            </thead>
            <tbody>
              {this.state.transactions.length === 0 && (
                <tr>
                  <td colSpan={cols}>This order has no transactions associated with it.</td>
                </tr>
              )}
              {this.state.transactions.map(t => (
                <tr key={t.id}>
                  <td>{t.number}</td>
                  <td>
                    <Money value={t.amount} />
                  </td>
                  <td>
                    <Money value={t.fee} />
                  </td>
                  <td>
                    {t.type === Transaction.Type.CHECK && <span>Check</span>}
                    {t.type === Transaction.Type.CHARGE && <span>Charge</span>}
                    {t.type === Transaction.Type.WIRE && <span>Wire</span>}
                    {t.type === Transaction.Type.RECEIVABLE && <span>Receivable</span>}
                    {t.type === Transaction.Type.REFUND_CHARGE && <span>Refund (Charge)</span>}
                    {t.type === Transaction.Type.REFUND_CHECK && <span>Refund (Check)</span>}
                    {t.type === Transaction.Type.CANCEL && <span>Cancellation</span>}
                    {t.type === Transaction.Type.ADJUSTMENT && <span>Adjustment</span>}
                  </td>
                  <td>
                    {t.checkNumber} {t.stripeChargeId} {t.type === Transaction.Type.ADJUSTMENT && t.description}
                  </td>
                  <td>
                    <Datetime at={t.date} format="M/D/YY" />
                  </td>
                  <td>
                    <Datetime at={t.at} />
                  </td>
                  <td>
                    <MemberLink member={this.props.appState.entities.member[t.createId!]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <PaymentDialog
          isOpen={this.state.dialog === OrdersDetailsDialog.PAYMENT}
          onSave={this.handleSavePayment}
          onCancel={this.handleCancelDialog}
        />
        <RefundDialog
          isOpen={this.state.dialog === OrdersDetailsDialog.REFUND}
          onSave={this.handleSubmitRefund}
          onCancel={this.handleCancelDialog}
          refundAmount={refundableAmount}
          orderAmount={this.state.order!.billAmount!}
          includeCheckNumber={!hasChargeTransaction}
          isLoading={this.state.dialogLoading}
          error={this.state.dialogError}
          appState={this.props.appState}
          order={this.state.order}
        />
        <ChargeDialog
          isOpen={this.state.dialog === OrdersDetailsDialog.CHARGE}
          accountId={order.accountId!}
          orderId={order.id!}
          onSubmit={this.handleSubmitCharge}
          onCancel={this.handleCancelDialog}
          chargeAmount={dueAmount}
        />
        <AdjustmentDialog
          isOpen={this.state.dialog === OrdersDetailsDialog.ADJUSTMENT}
          onSave={this.handleSubmitAdjustment}
          onCancel={this.handleCancelDialog}
          isLoading={this.state.dialogLoading}
          error={this.state.dialogError}
          appState={this.props.appState}
          order={this.state.order}
        />
        <ConfirmDialog
          isOpen={this.state.dialog === OrdersDetailsDialog.CANCEL}
          onConfirm={this.handleSubmitCancel}
          onCancel={this.handleCancelDialog}
          title="Cancel Order"
          message="Are you sure you want to cancel this order?"
          error={this.state.dialogError}
        />
        {member && (
          <ConfirmDialog
            isOpen={this.state.dialog === OrdersDetailsDialog.SEND_LETTER}
            onConfirm={this.handleSubmitSendLetter}
            onCancel={this.handleCancelDialog}
            title="Send Letter"
            message={
              <div>
                <div>Are you sure you want to send a letter to the following address?</div>
                <div>
                  <br />
                </div>
                <div>{member.name}</div>
                <AddressBlock address={member.billingAddress ? member.billingAddress : member.workAddress} />
              </div>
            }
            error={this.state.dialogError}
            isLoading={this.state.dialogLoading}
            success={this.state.dialogSuccess}
            loadingMessage="Sending letter..."
          />
        )}
        <EditAddressDialog
          address={
            this.state.dialog === OrdersDetailsDialog.UPDATE_SHIPPING_ADDRESS
              ? this.state.editShippingAddress
              : undefined
          }
          onSave={this.handleSaveAddress}
          onCancel={this.handleCancelDialog}
        />
        <SendEmailDialog
          isOpen={this.state.dialog === OrdersDetailsDialog.SEND_EMAIL}
          onSubmit={this.handleSubmitSendEmail}
          onCancel={this.handleCancelDialog}
          title="Send Receipt / Invoice Email"
          email={member ? member.email : undefined}
          isLoading={this.state.dialogLoading}
          errorMessage={this.state.dialogError}
          successMessage={this.state.dialogSuccess}
        />
      </Page>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orders/OrdersDetails.tsx